import { UnknownObject } from 'libs/end-to-end/src/generic';
import { OrgDBScopedMergeRealTimeWritebackConfig } from 'libs/end-to-end/src/types/databases/merge-realtime-writebacks/merge-real-time-writeback-config';
import { OrgDBScopedWriteableMergeRealTimeWritebackConfig } from 'libs/end-to-end/src/types/databases/merge-realtime-writebacks/merge-real-time-writeback-config';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { SimpleInput } from 'apps/sigma2-ui/src/components/ui/SimpleInput';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { useUndoableState } from 'apps/sigma2-ui/src/lib/hooks/useUndoableState';
import { DataSetSelector } from 'apps/sigma2-ui/src/data-wizard/components/DataSetSelector';
import { isMergeAtsIntegrationDataSet } from 'libs/end-to-end/src/is-merge-ats-integration-data-set';
import { DataSetCompositeRecipeMergeResumeGradingConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeMergeResumeParserConfigComponent';
import { RubricConstructsModal } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeMergeResumeParserConfigComponent';
import { useRubricConstructsModalConfigs } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeMergeResumeParserConfigComponent';
import { CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { ConfigureAIInterviewerWriteBackStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureAIInterviewerWriteBackDetails';
import { useListOrganizationSecretsPattern } from 'apps/sigma2-ui/src/api/list-organization-secrets-pattern';
import { useListDataSetsPattern } from 'apps/sigma2-ui/src/api/list-data-sets-pattern';
import { ConfigureHiringReadinessScoreWriteBackStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureHiringReadinessScoreWriteBackStrategy';
import { ConfigureHiringReadinessExplanationWriteBackStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureHiringReadinessExplanationWriteBackStrategy';
import { ConfigureWriteBackLeverStrategy } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackLeverStrategy';
import { SubTabsTabDefinition } from 'apps/sigma2-ui/src/lib/hooks/useSubTabGroup';
import { useSubTabs } from 'apps/sigma2-ui/src/lib/hooks/useSubTabGroup';
import { SubTabs } from 'apps/sigma2-ui/src/components/ui/SubTabs';
import { ConfigureWriteBackMode } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackMode';
import { EnabledDisabledBanner } from 'apps/sigma2-ui/src/components/ui/EnabledDisabledBanner';

const TabHeader = ({
  text,
  isEnabled,
  bottomContent,
}: {
  text: string;
  isEnabled: boolean;
  bottomContent?: React.ReactNode;
}) => {
  return (
    <Spacing size={'xs'} direction={'vertical'} className={'items-start'}>
      <Spacing size={'md'} direction={'horizontal'} className={'items-center'}>
        <S2Typography variant={'headline-6-alt'} translatedContent={text} />
        <EnabledDisabledBanner isEnabled={isEnabled} />
      </Spacing>
      {bottomContent}
    </Spacing>
  );
};

export const MergeRealtimeWritebackConfigDetailsPage = () => {
  const { organizationId, databaseId, writebackConfigId } = useParams();

  const navigate = useNavigateSafe();

  if (!organizationId || !databaseId || !writebackConfigId) {
    throw new Error(
      'MergeRealtimeWritebackConfigDetailsPage requires organizationId, databaseId, and writebackConfigId in the URL parameters.',
    );
  }

  const getUrlFn = useCallback(
    () =>
      `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/merge-realtime-writeback-configs/${writebackConfigId}`,
    [databaseId, organizationId, writebackConfigId],
  );
  const readAllPattern = useSimpleReadPattern({
    getUrlFn,
    canRead: true,
    responseSchema: OrgDBScopedMergeRealTimeWritebackConfig,
  });

  const isReadLoading = useMemo(() => {
    return readAllPattern.status === 'loading';
  }, [readAllPattern]);

  const updatePattern = useSimpleWritePattern({
    getUrlFn,
    canWrite: !isReadLoading,
    writeSchema: OrgDBScopedWriteableMergeRealTimeWritebackConfig,
    responseSchema: UnknownObject,
    method: 'PUT',
  });

  const deletePattern = useSimpleWritePattern({
    getUrlFn,
    canWrite: !isReadLoading,
    writeSchema: UnknownObject,
    responseSchema: UnknownObject,
    method: 'DELETE',
  });

  const listDataSetsPattern = useListDataSetsPattern({
    organizationId,
    databaseId,
  });

  const { state: localConfigPayload, set: setLocalConfigPayload } =
    useUndoableState<OrgDBScopedMergeRealTimeWritebackConfig | null>(null);

  useEffect(() => {
    if (readAllPattern.status === 'done') {
      setLocalConfigPayload(readAllPattern.entity);
    }
  }, [readAllPattern, setLocalConfigPayload]);

  const setLocalConfigPayloadWithResumeGradingRubrics = useCallback(
    (
      rubricsByJobTitle: CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric[],
    ) => {
      if (localConfigPayload && localConfigPayload.resumeGradingConfig) {
        setLocalConfigPayload({
          ...localConfigPayload,
          resumeGradingConfig: {
            ...localConfigPayload.resumeGradingConfig,
            rubricsByJobTitle,
          },
        });
      }
    },
    [localConfigPayload, setLocalConfigPayload],
  );

  const setLocalConfigPayloadWithAIInterviewerRubrics = useCallback(
    (
      rubricsByJobTitle: CompositeDataSetMergeResumeParsingRecipeResumeGradingRubric[],
    ) => {
      if (localConfigPayload && localConfigPayload.aiInterviewerConfig) {
        setLocalConfigPayload({
          ...localConfigPayload,
          aiInterviewerConfig: {
            ...localConfigPayload.aiInterviewerConfig,
            rubricsByJobTitle,
          },
        });
      }
    },
    [localConfigPayload, setLocalConfigPayload],
  );

  const resumeGradingRubricConstructsModalConfigs =
    useRubricConstructsModalConfigs({
      organizationId,
      databaseId,
      rubricsByJobTitle:
        localConfigPayload?.resumeGradingConfig?.rubricsByJobTitle || [],
      setRubricsByJobTitle: setLocalConfigPayloadWithResumeGradingRubrics,
    });

  const aiInterviewerRubricConstructsModalConfigs =
    useRubricConstructsModalConfigs({
      organizationId,
      databaseId,
      rubricsByJobTitle:
        localConfigPayload?.aiInterviewerConfig?.rubricsByJobTitle || [],
      setRubricsByJobTitle: setLocalConfigPayloadWithAIInterviewerRubrics,
    });

  const listSecretsPattern = useListOrganizationSecretsPattern({
    organizationId,
  });

  const resumeParsingSubTab = useCallback(
    (
      payload: OrgDBScopedMergeRealTimeWritebackConfig,
    ): SubTabsTabDefinition => {
      const header = () => (
        <TabHeader
          text={'Resume Parsing'}
          isEnabled={payload.resumeParserEnabled}
        />
      );

      const body = () => (
        <S2Checkbox
          checked={payload.resumeParserEnabled}
          onChange={() => {
            setLocalConfigPayload({
              ...payload,
              resumeParserEnabled: !payload.resumeParserEnabled,
            });
          }}
        >
          Resume Parsing Enabled
        </S2Checkbox>
      );

      return {
        header,
        body,
      };
    },
    [setLocalConfigPayload],
  );

  const resumeGradingSubTab = useCallback(
    (
      payload: OrgDBScopedMergeRealTimeWritebackConfig,
    ): SubTabsTabDefinition => {
      const filterConfigCount = payload.resumeGradingConfig
        ? (payload.resumeGradingConfig.rubricsByJobTitle?.length ?? 0)
        : null;
      const header = () => (
        <TabHeader
          text={'Resume Grading'}
          isEnabled={payload.resumeGradingConfig !== null}
          bottomContent={
            filterConfigCount ? (
              <S2Typography
                variant={'body-7'}
                translatedContent={`${filterConfigCount} Configs`}
              />
            ) : null
          }
        />
      );

      const body = () => (
        <Spacing size={'md'} direction={'vertical'}>
          <S2Checkbox
            checked={payload.resumeGradingConfig !== null}
            onChange={() => {
              if (payload.resumeGradingConfig) {
                setLocalConfigPayload({
                  ...payload,
                  resumeGradingConfig: null,
                });
              } else {
                setLocalConfigPayload({
                  ...payload,
                  resumeGradingConfig: {
                    openAiModel: 'gpt-4o-mini',
                    rubricsByJobTitle: [],
                  },
                });
              }
            }}
          >
            Resume Grading Enabled
          </S2Checkbox>

          {payload.resumeGradingConfig && (
            <DataSetCompositeRecipeMergeResumeGradingConfigComponent
              config={payload.resumeGradingConfig}
              setConfig={(value) => {
                setLocalConfigPayload({
                  ...payload,
                  resumeGradingConfig: value,
                });
              }}
              setShowConstructsModal={
                resumeGradingRubricConstructsModalConfigs.setShowConstructsModal
              }
              setCurrentRubricConfigIndex={
                resumeGradingRubricConstructsModalConfigs.setCurrentRubricConfigIndex
              }
              rubricConfigOps={
                resumeGradingRubricConstructsModalConfigs.rubricConfigOps
              }
              useRubricConstructsDataPayload={
                resumeGradingRubricConstructsModalConfigs.useRubricConstructsDataPayload
              }
            />
          )}
        </Spacing>
      );

      return {
        header,
        body,
      };
    },
    [setLocalConfigPayload, resumeGradingRubricConstructsModalConfigs],
  );

  const aiInterviewerConfigSubTab = useCallback(
    (
      payload: OrgDBScopedMergeRealTimeWritebackConfig,
    ): SubTabsTabDefinition => {
      const header = () => (
        <TabHeader
          text={'AI Interviewer'}
          isEnabled={payload.aiInterviewerConfig !== null}
        />
      );

      const body = () => (
        <Spacing size={'md'} direction={'vertical'}>
          <S2Checkbox
            checked={payload.aiInterviewerConfig !== null}
            onChange={() => {
              if (payload.aiInterviewerConfig) {
                setLocalConfigPayload({
                  ...payload,
                  aiInterviewerConfig: null,
                });
              } else {
                setLocalConfigPayload({
                  ...payload,
                  aiInterviewerConfig: {
                    openAiModel: 'gpt-4o-mini',
                    rubricsByJobTitle: [],
                  },
                });
              }
            }}
          >
            AI Interviewer Enabled
          </S2Checkbox>
          {payload.aiInterviewerConfig && (
            <DataSetCompositeRecipeMergeResumeGradingConfigComponent
              config={payload.aiInterviewerConfig}
              setConfig={(value) => {
                setLocalConfigPayload({
                  ...payload,
                  aiInterviewerConfig: value,
                });
              }}
              setShowConstructsModal={
                aiInterviewerRubricConstructsModalConfigs.setShowConstructsModal
              }
              setCurrentRubricConfigIndex={
                aiInterviewerRubricConstructsModalConfigs.setCurrentRubricConfigIndex
              }
              rubricConfigOps={
                aiInterviewerRubricConstructsModalConfigs.rubricConfigOps
              }
              useRubricConstructsDataPayload={
                aiInterviewerRubricConstructsModalConfigs.useRubricConstructsDataPayload
              }
            />
          )}
        </Spacing>
      );

      return {
        header,
        body,
      };
    },
    [setLocalConfigPayload, aiInterviewerRubricConstructsModalConfigs],
  );

  const aiInterviewerWritebackConfigSubTab = useCallback(
    (
      payload: OrgDBScopedMergeRealTimeWritebackConfig,
    ): SubTabsTabDefinition => {
      const header = () => (
        <TabHeader
          text={'AI Interviewer Writeback'}
          isEnabled={payload.aiInterviewerWritebackConfig !== null}
        />
      );

      const body = () => (
        <Spacing size={'md'} direction={'vertical'}>
          <S2Checkbox
            checked={payload.aiInterviewerWritebackConfig !== null}
            onChange={() => {
              if (payload.aiInterviewerWritebackConfig) {
                setLocalConfigPayload({
                  ...payload,
                  aiInterviewerWritebackConfig: null,
                });
              } else {
                setLocalConfigPayload({
                  ...payload,
                  aiInterviewerWritebackConfig: {
                    strategy: {
                      type: 'workdayViaWs',
                    },
                    mode: 'normal',
                  },
                });
              }
            }}
          >
            AI Interviewer Writeback Enabled
          </S2Checkbox>

          {payload.aiInterviewerWritebackConfig && (
            <Spacing size={'sm'} direction={'vertical'}>
              <ConfigureWriteBackMode
                mode={payload.aiInterviewerWritebackConfig.mode}
                updateMode={(mode) => {
                  if (payload.aiInterviewerWritebackConfig && mode) {
                    setLocalConfigPayload({
                      ...payload,
                      aiInterviewerWritebackConfig: {
                        ...payload.aiInterviewerWritebackConfig,
                        mode,
                      },
                    });
                  }
                }}
              />
              <ConfigureAIInterviewerWriteBackStrategy
                strategy={payload.aiInterviewerWritebackConfig.strategy}
                updateStrategy={(strategy) => {
                  if (payload.aiInterviewerWritebackConfig && strategy) {
                    setLocalConfigPayload({
                      ...payload,
                      aiInterviewerWritebackConfig: {
                        ...payload.aiInterviewerWritebackConfig,
                        strategy,
                      },
                    });
                  }
                }}
                organizationId={organizationId}
                listSecretsPattern={listSecretsPattern}
                listDataSetsPattern={listDataSetsPattern}
              />
            </Spacing>
          )}
        </Spacing>
      );
      return {
        header,
        body,
      };
    },
    [
      setLocalConfigPayload,
      organizationId,
      listSecretsPattern,
      listDataSetsPattern,
    ],
  );

  const resumeGradingWritebackSubTab = useCallback(
    (
      payload: OrgDBScopedMergeRealTimeWritebackConfig,
    ): SubTabsTabDefinition => {
      const header = () => (
        <TabHeader
          text={'Resume Grading Writeback'}
          isEnabled={payload.resumeGradingWritebackConfig !== null}
        />
      );

      const body = () =>
        payload ? (
          <Spacing size={'md'} direction={'vertical'}>
            <S2Checkbox
              checked={payload.resumeGradingWritebackConfig !== null}
              onChange={() => {
                if (payload.resumeGradingWritebackConfig) {
                  setLocalConfigPayload({
                    ...payload,
                    resumeGradingWritebackConfig: null,
                  });
                } else {
                  setLocalConfigPayload({
                    ...payload,
                    resumeGradingWritebackConfig: {
                      strategy: {
                        type: 'lever',
                      },
                      mode: 'normal',
                    },
                  });
                }
              }}
            >
              Resume Grading Writeback Enabled
            </S2Checkbox>

            {payload.resumeGradingWritebackConfig && (
              <Spacing size={'sm'} direction={'vertical'}>
                <ConfigureWriteBackMode
                  mode={payload.resumeGradingWritebackConfig.mode}
                  updateMode={(mode) => {
                    if (payload.resumeGradingWritebackConfig && mode) {
                      setLocalConfigPayload({
                        ...payload,
                        resumeGradingWritebackConfig: {
                          ...payload.resumeGradingWritebackConfig,
                          mode,
                        },
                      });
                    }
                  }}
                />
                <ConfigureWriteBackLeverStrategy
                  strategy={payload.resumeGradingWritebackConfig.strategy}
                  updateStrategy={(strategy) => {
                    if (payload.resumeGradingWritebackConfig && strategy) {
                      setLocalConfigPayload({
                        ...payload,
                        resumeGradingWritebackConfig: {
                          ...payload.resumeGradingWritebackConfig,
                          strategy,
                        },
                      });
                    }
                  }}
                  organizationId={organizationId}
                  listSecretsPattern={listSecretsPattern}
                  listDataSetsPattern={listDataSetsPattern}
                />
                <S2Checkbox
                  checked={
                    payload.resumeGradingWritebackConfig.explanationsEnabled ===
                    true
                  }
                  onChange={() => {
                    if (payload.resumeGradingWritebackConfig) {
                      setLocalConfigPayload({
                        ...payload,
                        resumeGradingWritebackConfig: {
                          ...payload.resumeGradingWritebackConfig,
                          explanationsEnabled:
                            !payload.resumeGradingWritebackConfig
                              .explanationsEnabled,
                        },
                      });
                    }
                  }}
                >
                  Include Score Explanation in writeback note
                </S2Checkbox>
              </Spacing>
            )}
          </Spacing>
        ) : null;

      return {
        header,
        body,
      };
    },
    [
      setLocalConfigPayload,
      organizationId,
      listSecretsPattern,
      listDataSetsPattern,
    ],
  );

  const hiringReadinessScoreWritebackSubTab = useCallback(
    (
      payload: OrgDBScopedMergeRealTimeWritebackConfig,
    ): SubTabsTabDefinition => {
      const header = () => (
        <TabHeader
          text={'Hiring Readiness Score Writeback'}
          isEnabled={payload.hiringReadinessWritebackConfig !== null}
        />
      );

      const body = () =>
        payload ? (
          <Spacing size={'md'} direction={'vertical'}>
            <S2Checkbox
              checked={payload.hiringReadinessWritebackConfig !== null}
              onChange={() => {
                if (payload.hiringReadinessWritebackConfig) {
                  setLocalConfigPayload({
                    ...payload,
                    hiringReadinessWritebackConfig: null,
                  });
                } else {
                  setLocalConfigPayload({
                    ...payload,
                    hiringReadinessWritebackConfig: {
                      strategy: {
                        type: 'lever',
                      },
                      mode: 'normal',
                    },
                  });
                }
              }}
            >
              Hiring Readiness Score Writeback Enabled
            </S2Checkbox>

            {payload.hiringReadinessWritebackConfig && (
              <Spacing size={'sm'} direction={'vertical'}>
                <ConfigureWriteBackMode
                  mode={payload.hiringReadinessWritebackConfig.mode}
                  updateMode={(mode) => {
                    if (payload.hiringReadinessWritebackConfig && mode) {
                      setLocalConfigPayload({
                        ...payload,
                        hiringReadinessWritebackConfig: {
                          ...payload.hiringReadinessWritebackConfig,
                          mode,
                        },
                      });
                    }
                  }}
                />
                <ConfigureHiringReadinessScoreWriteBackStrategy
                  strategy={payload.hiringReadinessWritebackConfig.strategy}
                  updateStrategy={(strategy) => {
                    if (payload.hiringReadinessWritebackConfig && strategy) {
                      setLocalConfigPayload({
                        ...payload,
                        hiringReadinessWritebackConfig: {
                          ...payload.hiringReadinessWritebackConfig,
                          strategy,
                        },
                      });
                    }
                  }}
                  organizationId={organizationId}
                  listSecretsPattern={listSecretsPattern}
                  listDataSetsPattern={listDataSetsPattern}
                />
              </Spacing>
            )}
          </Spacing>
        ) : null;

      return {
        header,
        body,
      };
    },
    [
      setLocalConfigPayload,
      organizationId,
      listSecretsPattern,
      listDataSetsPattern,
    ],
  );

  const hiringReadinessExplanationWritebackSubTab = useCallback(
    (
      payload: OrgDBScopedMergeRealTimeWritebackConfig,
    ): SubTabsTabDefinition => {
      const header = () => (
        <TabHeader
          text={'Hiring Readiness Explanation Writeback'}
          isEnabled={
            payload.hiringReadinessExplanationWritebackConfig !== undefined
          }
        />
      );

      const body = () =>
        payload ? (
          <Spacing size={'md'} direction={'vertical'}>
            <S2Checkbox
              checked={
                payload.hiringReadinessExplanationWritebackConfig !== undefined
              }
              onChange={() => {
                if (payload.hiringReadinessExplanationWritebackConfig) {
                  setLocalConfigPayload({
                    ...payload,
                    hiringReadinessExplanationWritebackConfig: undefined,
                  });
                } else {
                  setLocalConfigPayload({
                    ...payload,
                    hiringReadinessExplanationWritebackConfig: {
                      strategy: {
                        type: 'lever',
                      },
                      mode: 'normal',
                    },
                  });
                }
              }}
            >
              Hiring Readiness Explanation Writeback Enabled
            </S2Checkbox>

            {payload.hiringReadinessExplanationWritebackConfig && (
              <Spacing size={'sm'} direction={'vertical'}>
                <ConfigureWriteBackMode
                  mode={payload.hiringReadinessExplanationWritebackConfig.mode}
                  updateMode={(mode) => {
                    if (
                      payload.hiringReadinessExplanationWritebackConfig &&
                      mode
                    ) {
                      setLocalConfigPayload({
                        ...payload,
                        hiringReadinessExplanationWritebackConfig: {
                          ...payload.hiringReadinessExplanationWritebackConfig,
                          mode,
                        },
                      });
                    }
                  }}
                />
                <ConfigureHiringReadinessExplanationWriteBackStrategy
                  strategy={
                    payload.hiringReadinessExplanationWritebackConfig.strategy
                  }
                  updateStrategy={(strategy) => {
                    if (
                      payload.hiringReadinessExplanationWritebackConfig &&
                      strategy
                    ) {
                      setLocalConfigPayload({
                        ...payload,
                        hiringReadinessExplanationWritebackConfig: {
                          ...payload.hiringReadinessExplanationWritebackConfig,
                          strategy,
                        },
                      });
                    }
                  }}
                  organizationId={organizationId}
                  listSecretsPattern={listSecretsPattern}
                  listDataSetsPattern={listDataSetsPattern}
                />
              </Spacing>
            )}
          </Spacing>
        ) : null;

      return {
        header,
        body,
      };
    },
    [
      setLocalConfigPayload,
      organizationId,
      listSecretsPattern,
      listDataSetsPattern,
    ],
  );

  const topSubTabDefinitions = useMemo(() => {
    if (!localConfigPayload) {
      return [];
    }

    return [
      resumeParsingSubTab(localConfigPayload),
      resumeGradingSubTab(localConfigPayload),
      aiInterviewerConfigSubTab(localConfigPayload),
    ];
  }, [
    resumeParsingSubTab,
    resumeGradingSubTab,
    aiInterviewerConfigSubTab,
    localConfigPayload,
  ]);

  const bottomSubTabDefinitions = useMemo(() => {
    if (!localConfigPayload) {
      return [];
    }

    return [
      aiInterviewerWritebackConfigSubTab(localConfigPayload),
      resumeGradingWritebackSubTab(localConfigPayload),
      hiringReadinessScoreWritebackSubTab(localConfigPayload),
      hiringReadinessExplanationWritebackSubTab(localConfigPayload),
    ];
  }, [
    aiInterviewerWritebackConfigSubTab,
    resumeGradingWritebackSubTab,
    hiringReadinessScoreWritebackSubTab,
    hiringReadinessExplanationWritebackSubTab,
    localConfigPayload,
  ]);

  const topSubTabsPayload = useSubTabs({
    tabDefinitions: topSubTabDefinitions,
  });

  const bottomSubTabsPayload = useSubTabs({
    tabDefinitions: bottomSubTabDefinitions,
  });

  return (
    <AdminLayout currentPage="MergeRealtimeConfigDetailsPage">
      <RubricConstructsModal {...resumeGradingRubricConstructsModalConfigs} />
      <RubricConstructsModal {...aiInterviewerRubricConstructsModalConfigs} />
      <Spacing direction="vertical" size="md">
        <AdminBreadcrumbs
          items={[
            { label: 'Home', to: '/admin' },
            { label: 'Organizations', to: '/organizations' },
            {
              label: 'Databases',
              to: `/organizations/${organizationId}/databases`,
            },
            {
              label: 'Edit Database',
              to: `/organizations/${organizationId}/databases/edit/${databaseId}`,
            },
            {
              label: 'Merge Realtime Writeback Configs',
              to: `/organizations/${organizationId}/databases/${databaseId}/merge-realtime-writeback-configs`,
            },
            {
              label: 'Edit Config',
              to: `/organizations/${organizationId}/databases/${databaseId}/merge-realtime-writeback-configs/${writebackConfigId}`,
            },
          ]}
        />

        <S2Typography
          variant={'headline-4-alt'}
          translatedContent={'Edit Config'}
        />

        <SimpleInput
          label="Name"
          value={localConfigPayload?.name || ''}
          onChange={(value) => {
            if (localConfigPayload) {
              setLocalConfigPayload({
                ...localConfigPayload,
                name: value,
              });
            }
          }}
        />

        <SimpleInput
          label="Linked Account ID"
          value={localConfigPayload?.linkedAccountId || ''}
          onChange={(value) => {
            if (localConfigPayload) {
              setLocalConfigPayload({
                ...localConfigPayload,
                linkedAccountId: value,
              });
            }
          }}
        />

        <Spacing direction="horizontal" size="md" className="items-center">
          <S2Typography
            variant={'headline-7-alt'}
            translatedContent={'Choose a Data Set'}
          />

          <DataSetSelector
            label=""
            dataSetId={localConfigPayload?.dataSetId || null}
            setDataSetId={(value) => {
              if (localConfigPayload && value) {
                setLocalConfigPayload({
                  ...localConfigPayload,
                  dataSetId: value,
                });
              }
            }}
            dataSets={
              listDataSetsPattern.status === 'done'
                ? listDataSetsPattern.entity.dataSets.filter(
                    isMergeAtsIntegrationDataSet,
                  )
                : []
            }
          />
        </Spacing>

        <S2Typography
          variant={'headline-4-alt'}
          translatedContent={'Evaluation Configs'}
        />

        <SubTabs {...topSubTabsPayload} />

        <S2Typography
          variant={'headline-4-alt'}
          translatedContent={'Writeback Configs'}
        />

        <SubTabs {...bottomSubTabsPayload} />

        <Spacing direction="horizontal" size="md">
          <Button
            variant="primary"
            size="small"
            behavior={{
              type: 'onClick',
              onClick: () => {
                if (updatePattern.canWrite && localConfigPayload) {
                  updatePattern.saveEntity(localConfigPayload, () => {
                    if (readAllPattern.status !== 'loading') {
                      readAllPattern.reloadEntity();
                    }
                  });
                }
              },
            }}
          >
            Save
          </Button>
          <Button
            variant="primary"
            size="small"
            behavior={{
              type: 'onClick',
              onClick: () => {
                if (updatePattern.canWrite && localConfigPayload) {
                  updatePattern.saveEntity(localConfigPayload, () => {
                    navigate(
                      `/organizations/${organizationId}/databases/${databaseId}/merge-realtime-writeback-configs`,
                    );
                  });
                }
              },
            }}
          >
            Save and Exit
          </Button>
          <Button
            variant="destructive"
            size="small"
            behavior={{
              type: 'onClick',
              onClick: () => {
                if (deletePattern.canWrite) {
                  deletePattern.saveEntity({}, () => {
                    navigate(
                      `/organizations/${organizationId}/databases/${databaseId}/merge-realtime-writeback-configs`,
                    );
                  });
                }
              },
            }}
          >
            Delete
          </Button>
        </Spacing>
      </Spacing>
    </AdminLayout>
  );
};
